/**
 * MasterDetailLayout — Resizable two-panel CSS grid layout
 *
 * Left panel: entity list (table or card grid)
 * Right panel: inline entity detail (or placeholder when nothing selected)
 *
 * Divider position persisted in localStorage.
 * Responsive: collapses to single column on small screens.
 */

import { useRef, useState, useCallback, useEffect } from 'react';
import { Text, Center, Stack } from '@mantine/core';
import { IconClick } from '@tabler/icons-react';

interface MasterDetailLayoutProps {
  listContent: React.ReactNode;
  detailContent: React.ReactNode | null;
  storageKey?: string;
  emptyDetailMessage?: string;
}

const STORAGE_PREFIX = 'ferp-master-detail-';
const MIN_PERCENT = 25;
const MAX_PERCENT = 75;
const DEFAULT_PERCENT = 55;

function loadDividerPosition(key: string): number {
  try {
    const stored = localStorage.getItem(`${STORAGE_PREFIX}${key}`);
    if (stored) {
      const val = Number(stored);
      if (val >= MIN_PERCENT && val <= MAX_PERCENT) return val;
    }
  } catch { /* */ }
  return DEFAULT_PERCENT;
}

function saveDividerPosition(key: string, position: number) {
  try {
    localStorage.setItem(`${STORAGE_PREFIX}${key}`, String(position));
  } catch { /* */ }
}

export function MasterDetailLayout({
  listContent,
  detailContent,
  storageKey = 'default',
  emptyDetailMessage = 'Select an entity to view details',
}: MasterDetailLayoutProps) {
  const [leftPercent, setLeftPercent] = useState(() => loadDividerPosition(storageKey));
  const containerRef = useRef<HTMLDivElement>(null);
  const isDragging = useRef(false);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    isDragging.current = true;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  }, []);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isDragging.current || !containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      const percent = ((e.clientX - rect.left) / rect.width) * 100;
      const clamped = Math.min(MAX_PERCENT, Math.max(MIN_PERCENT, percent));
      setLeftPercent(clamped);
    };

    const handleMouseUp = () => {
      if (!isDragging.current) return;
      isDragging.current = false;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      setLeftPercent((current) => {
        saveDividerPosition(storageKey, current);
        return current;
      });
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [storageKey]);

  return (
    <div
      ref={containerRef}
      style={{
        display: 'grid',
        gridTemplateColumns: `${leftPercent}% 4px 1fr`,
        height: '100%',
        minHeight: '400px',
        gap: 0,
      }}
    >
      {/* Left panel: list */}
      <div style={{ overflow: 'auto', minWidth: 0 }}>
        {listContent}
      </div>

      {/* Draggable divider */}
      <div
        onMouseDown={handleMouseDown}
        style={{
          cursor: 'col-resize',
          background: 'var(--mantine-color-default-border)',
          transition: isDragging.current ? 'none' : 'background 0.15s',
          width: 4,
        }}
        onMouseEnter={(e) => {
          (e.currentTarget as HTMLElement).style.background = 'var(--mantine-color-blue-5)';
        }}
        onMouseLeave={(e) => {
          if (!isDragging.current) {
            (e.currentTarget as HTMLElement).style.background = 'var(--mantine-color-default-border)';
          }
        }}
        role="separator"
        aria-orientation="vertical"
        aria-label="Resize panels"
      />

      {/* Right panel: detail or placeholder */}
      <div style={{ overflow: 'auto', minWidth: 0 }}>
        {detailContent ?? (
          <Center h="100%">
            <Stack align="center" gap="xs">
              <IconClick size={32} color="var(--mantine-color-dimmed)" />
              <Text c="dimmed" size="sm">{emptyDetailMessage}</Text>
            </Stack>
          </Center>
        )}
      </div>
    </div>
  );
}
